import { Annotation } from '../models/annotation.model';
import { AnnotationRelation } from '../models/annotation-relation.model';
import { Map } from 'immutable';

type Point = { x: number; y: number };

function getCenter(ann: Annotation): Point | null {
  if (ann.bounds && ann.bounds.length > 0 && ann.bounds[0].bbox) {
    const b = ann.bounds[0].bbox;
    return { x: (b[0] + b[2]) / 2, y: (b[1] + b[3]) / 2 };
  }
  if (typeof ann.start === 'number' && typeof ann.end === 'number') {
    return { x: ann.start, y: 0 };
  }
  return null;
}

function spatialDistance(a: Point, b: Point): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.sqrt(dx * dx + dy * dy);
}

function assignSpatialColors(
  ids: string[],
  annotations: Map<string, Annotation>,
  colorMap: { [id: string]: number[] },
): void {
  if (ids.length === 0) return;

  // Compute centers
  const centers: { id: string; center: Point }[] = [];
  for (const id of ids) {
    const ann = annotations.get(id);
    if (!ann) continue;
    const c = getCenter(ann);
    if (c) {
      centers.push({ id, center: c });
    }
  }

  // For each annotation, find its K nearest spatial neighbors
  const K = Math.min(PALETTE.length, centers.length - 1);
  const neighbors: { [id: string]: string[] } = {};
  for (const a of centers) {
    const dists = centers
      .filter(b => b.id !== a.id)
      .map(b => ({ id: b.id, dist: spatialDistance(a.center, b.center) }))
      .sort((x, y) => x.dist - y.dist);
    neighbors[a.id] = dists.slice(0, K).map(d => d.id);
  }

  // Make neighbor graph symmetric: if A is B's neighbor, B must be A's
  for (const a of centers) {
    for (const nId of neighbors[a.id] || []) {
      if (!neighbors[nId]) neighbors[nId] = [];
      if (neighbors[nId].indexOf(a.id) < 0) {
        neighbors[nId].push(a.id);
      }
    }
  }

  // DSatur graph coloring: always color the node with the most
  // already-colored neighbors next, ensuring maximum constraint
  // awareness at each step.
  const uncolored = new Set(centers.map(c => c.id));
  while (uncolored.size > 0) {
    // Pick node with highest saturation (most distinct neighbor colors)
    let bestId = '';
    let bestSat = -1;
    let bestDegree = -1;
    for (const id of uncolored) {
      const nColors = new Set<string>();
      for (const nId of neighbors[id] || []) {
        if (colorMap[nId]) nColors.add(JSON.stringify(colorMap[nId]));
      }
      const sat = nColors.size;
      const deg = (neighbors[id] || []).length;
      if (sat > bestSat || (sat === bestSat && deg > bestDegree)) {
        bestSat = sat;
        bestDegree = deg;
        bestId = id;
      }
    }
    if (!bestId) break;
    uncolored.delete(bestId);

    const usedIndices = new Set<number>();
    for (const nId of neighbors[bestId] || []) {
      if (colorMap[nId]) {
        const key = JSON.stringify(colorMap[nId]);
        const pi = PALETTE.findIndex(c => JSON.stringify(c) === key);
        if (pi >= 0) usedIndices.add(pi);
      }
    }
    // Also exclude colors similar to used ones
    const usedWithSimilar = new Set(usedIndices);
    for (const pi of usedIndices) {
      for (const si of getSimilarIndices(pi)) {
        usedWithSimilar.add(si);
      }
    }
    let available = PALETTE.filter((_, i) => !usedWithSimilar.has(i));
    // Fall back to exact-only conflicts if similar exclusion leaves nothing
    if (available.length === 0) {
      available = PALETTE.filter((_, i) => !usedIndices.has(i));
    }
    // Pick a pseudo-random available color based on the node index
    const pool = available.length > 0 ? available : PALETTE;
    const idx = centers.findIndex(c => c.id === bestId);
    colorMap[bestId] = pool[idx % pool.length];
  }

  // Handle any IDs without centers (fallback)
  const assigned = new Set(centers.map(c => c.id));
  let fallbackIdx = 0;
  for (const id of ids) {
    if (!assigned.has(id)) {
      colorMap[id] = PALETTE[fallbackIdx % PALETTE.length];
      fallbackIdx++;
    }
  }
}

const PALETTE = [
  [230, 25, 75], // red
  [60, 180, 75], // green
  [67, 99, 216], // blue
  [245, 130, 49], // orange
  [145, 30, 180], // purple
  [66, 212, 244], // cyan
  [240, 50, 230], // magenta
  [191, 239, 69], // lime
  [0, 128, 128], // teal
  [220, 190, 55], // gold
  [170, 110, 40], // brown
  [128, 0, 0], // maroon
  [0, 75, 145], // navy
  [210, 105, 130], // rose
  [70, 153, 100], // forest
  [180, 120, 200], // lavender
];

// Pairs of similar colors that should be treated as conflicting
const SIMILAR_PAIRS: [number, number][] = [
  [0, 11], // red / maroon
  [1, 14], // green / forest
  [2, 12], // blue / navy
  [4, 15], // purple / lavender
  [4, 6], // purple / magenta
  [5, 8], // cyan / teal
  [3, 10], // orange / brown
  [7, 9], // lime / gold
  [0, 13], // red / rose
];

function getSimilarIndices(paletteIdx: number): number[] {
  const similar: number[] = [];
  for (const [a, b] of SIMILAR_PAIRS) {
    if (a === paletteIdx) similar.push(b);
    if (b === paletteIdx) similar.push(a);
  }
  return similar;
}

const GRAY = [200, 200, 200];

function colorStr(rgb: number[], lighten: number, alpha: number): string {
  const factor = Math.max(0, 1 - lighten);
  const r = Math.round(rgb[0] * factor + 255 * (1 - factor));
  const g = Math.round(rgb[1] * factor + 255 * (1 - factor));
  const b = Math.round(rgb[2] * factor + 255 * (1 - factor));
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function solidColorStr(rgb: number[]): string {
  return `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;
}

function colorForDepth(
  rgb: number[],
  relDepth: number,
): {
  color: string;
  borderColor: string;
  bottomBorderOnly?: boolean;
  dashed?: boolean;
} {
  if (relDepth === 0)
    return { color: colorStr(rgb, 0, 0.5), borderColor: solidColorStr(rgb) };
  if (relDepth === 1)
    return {
      color: colorStr(rgb, 0.55, 0.25),
      borderColor: solidColorStr(rgb),
      bottomBorderOnly: true,
    };
  if (relDepth === 2)
    return {
      color: colorStr(rgb, 0.55, 0.25),
      borderColor: solidColorStr(rgb),
      bottomBorderOnly: true,
      dashed: true,
    };
  return { color: colorStr(rgb, 0.55, 0.25), borderColor: '' };
}

type AnnotationColor = {
  id: string;
  color: string;
  borderColor: string;
  bottomBorderOnly?: boolean;
  dashed?: boolean;
};

export function computeRelationshipViewColors(
  annotations: Map<string, Annotation>,
  relations: AnnotationRelation[],
  focusType: string | null,
): AnnotationColor[] {
  const parentOf: { [id: string]: string } = {};
  const childrenOf: { [id: string]: string[] } = {};
  for (const rel of relations) {
    if (rel.rule.type === 'child-of') {
      parentOf[rel.left.id] = rel.right.id;
      if (!childrenOf[rel.right.id]) childrenOf[rel.right.id] = [];
      childrenOf[rel.right.id].push(rel.left.id);
    }
  }

  const depthCache: { [id: string]: number } = {};
  function getDepth(id: string): number {
    if (depthCache[id] !== undefined) return depthCache[id];
    const parent = parentOf[id];
    if (!parent) {
      depthCache[id] = 0;
      return 0;
    }
    depthCache[id] = getDepth(parent) + 1;
    return depthCache[id];
  }

  function getRootId(id: string): string {
    const parent = parentOf[id];
    if (!parent) return id;
    return getRootId(parent);
  }

  const allIds = Array.from(annotations.keys());

  let result: AnnotationColor[];
  if (focusType) {
    result = computeFocusColors(
      allIds,
      annotations,
      parentOf,
      childrenOf,
      focusType,
      getDepth,
    );
  } else {
    result = computeDefaultColors(allIds, annotations, getDepth, getRootId);
  }

  // Sort by depth so child CSS rules override parent rules
  result.sort((a, b) => getDepth(a.id) - getDepth(b.id));
  return result;
}

function computeDefaultColors(
  allIds: string[],
  annotations: Map<string, Annotation>,
  getDepth: (id: string) => number,
  getRootId: (id: string) => string,
): AnnotationColor[] {
  const rootColors: { [rootId: string]: number[] } = {};

  const roots = allIds
    .filter(id => getRootId(id) === id)
    .sort((a, b) => {
      const aStart = annotations.get(a)?.start || 0;
      const bStart = annotations.get(b)?.start || 0;
      return aStart - bStart;
    });

  // Assign colors using spatial graph coloring
  assignSpatialColors(roots, annotations, rootColors);

  const result: AnnotationColor[] = [];
  for (const id of allIds) {
    const rootId = getRootId(id);
    const baseColor = rootColors[rootId] || GRAY;
    const depth = getDepth(id);
    const cd = colorForDepth(baseColor, depth);
    result.push({
      id,
      color: cd.color,
      borderColor: cd.borderColor,
      bottomBorderOnly: cd.bottomBorderOnly,
      dashed: cd.dashed,
    });
  }

  return result;
}

function computeFocusColors(
  allIds: string[],
  annotations: Map<string, Annotation>,
  parentOf: { [id: string]: string },
  childrenOf: { [id: string]: string[] },
  focusType: string,
  getDepth: (id: string) => number,
): AnnotationColor[] {
  // Find ALL annotations of the focus type (not just roots)
  const focusAnns = allIds
    .filter(id => annotations.get(id)?.type === focusType)
    .sort((a, b) => {
      const aStart = annotations.get(a)?.start || 0;
      const bStart = annotations.get(b)?.start || 0;
      return aStart - bStart;
    });

  // Assign colors using spatial graph coloring
  const focusColors: { [id: string]: number[] } = {};
  assignSpatialColors(focusAnns, annotations, focusColors);

  // Collect descendants of each focus annotation
  const focusSet = new Set<string>();
  const ancestorFocusId: { [id: string]: string } = {};

  function collectDescendants(id: string, focusId: string) {
    const children = childrenOf[id] || [];
    for (const child of children) {
      focusSet.add(child);
      ancestorFocusId[child] = focusId;
      collectDescendants(child, focusId);
    }
  }

  for (const id of focusAnns) {
    focusSet.add(id);
    ancestorFocusId[id] = id;
    collectDescendants(id, id);
  }

  const result: AnnotationColor[] = [];
  for (const id of allIds) {
    if (focusSet.has(id)) {
      const focusId = ancestorFocusId[id];
      const baseColor = focusColors[focusId] || PALETTE[0];
      const focusDepth = getDepth(focusId);
      const relDepth = getDepth(id) - focusDepth;
      const cd = colorForDepth(baseColor, relDepth);
      result.push({
        id,
        color: cd.color,
        borderColor: cd.borderColor,
        bottomBorderOnly: cd.bottomBorderOnly,
        dashed: cd.dashed,
      });
    } else {
      result.push({ id, color: colorStr(GRAY, 0, 0.3), borderColor: '' });
    }
  }

  return result;
}
